import type {
  TransitionPresentation,
  TransitionPresentationComponentProps,
} from "@remotion/transitions";
import type { CSSProperties, FC } from "react";
import { AbsoluteFill, Easing, interpolate } from "remotion";

// The incoming scene wipes in to the middle of the frame, holds there so both
// scenes play side by side as a split screen, then wipes the rest of the way.
// A thin divider rides the wipe's edge to separate the two scenes.

// The outgoing scene stays full-frame and opaque underneath, so the frame is
// never see-through; only the incoming scene is clipped. Each scene is
// rendered once and nothing is filtered.

export type SplitScreenWipeDirection =
  "from-left" | "from-right" | "from-top" | "from-bottom";

export type SplitScreenWipeProps = {
  direction?: SplitScreenWipeDirection;
};

export const SPLIT_SCREEN_WIPE_IN_END = 0.3;
export const SPLIT_SCREEN_HOLD_END = 0.7;

export function getSplitScreenWipeReveal(presentationProgress: number) {
  return interpolate(
    presentationProgress,
    [0, SPLIT_SCREEN_WIPE_IN_END, SPLIT_SCREEN_HOLD_END, 1],
    [0, 0.5, 0.5, 1],
    {
      easing: Easing.inOut(Easing.cubic),
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    },
  );
}

export function getSplitScreenWipeEnteringStyle(
  presentationProgress: number,
  direction: SplitScreenWipeDirection = "from-right",
): CSSProperties {
  if (presentationProgress >= 1) {
    return {};
  }

  const hidden = `${(1 - getSplitScreenWipeReveal(presentationProgress)) * 100}%`;
  const inset = {
    "from-right": `inset(0 0 0 ${hidden})`,
    "from-left": `inset(0 ${hidden} 0 0)`,
    "from-bottom": `inset(${hidden} 0 0 0)`,
    "from-top": `inset(0 0 ${hidden} 0)`,
  }[direction];

  return { clipPath: inset };
}

export const SPLIT_SCREEN_DIVIDER_PX = 6;
const DIVIDER_COLOR = "black";

export function getSplitScreenWipeDividerStyle(
  presentationProgress: number,
  direction: SplitScreenWipeDirection = "from-right",
): CSSProperties | null {
  const reveal = getSplitScreenWipeReveal(presentationProgress);
  if (reveal <= 0 || reveal >= 1) {
    return null;
  }

  const edge =
    direction === "from-right" || direction === "from-bottom"
      ? (1 - reveal) * 100
      : reveal * 100;
  const position = `calc(${edge}% - ${SPLIT_SCREEN_DIVIDER_PX / 2}px)`;
  const vertical = direction === "from-left" || direction === "from-right";

  return {
    position: "absolute",
    backgroundColor: DIVIDER_COLOR,
    pointerEvents: "none",
    ...(vertical
      ? { top: 0, bottom: 0, left: position, width: SPLIT_SCREEN_DIVIDER_PX }
      : { left: 0, right: 0, top: position, height: SPLIT_SCREEN_DIVIDER_PX }),
  };
}

const SplitScreenWipePresentation: FC<
  TransitionPresentationComponentProps<SplitScreenWipeProps>
> = ({
  children,
  presentationProgress,
  presentationDirection,
  passedProps,
}) => {
  if (presentationDirection === "exiting") {
    return <AbsoluteFill>{children}</AbsoluteFill>;
  }

  const divider = getSplitScreenWipeDividerStyle(
    presentationProgress,
    passedProps.direction,
  );

  return (
    <AbsoluteFill>
      <AbsoluteFill
        style={getSplitScreenWipeEnteringStyle(
          presentationProgress,
          passedProps.direction,
        )}
      >
        {children}
      </AbsoluteFill>
      {divider ? <div style={divider} /> : null}
    </AbsoluteFill>
  );
};

export function splitScreenWipe(
  props: SplitScreenWipeProps = {},
): TransitionPresentation<SplitScreenWipeProps> {
  return { component: SplitScreenWipePresentation, props };
}
